// ABOUTME: Top navigation bar: brand mark, the search launcher with its results dropdown,
// ABOUTME: and the dashboard's add and edit-mode controls alongside the user menu.

import React, { useId } from "react";
import { Link } from "react-router-dom";
import UserMenu from "./UserMenu";
import LauncherResults, { launcherOptionId } from "./LauncherResults";
import { useLauncher } from "../hooks/useLauncher";
import { useUIStore } from "../stores/ui";
import type { DashboardItem } from "../services/dashboardService";

interface HeaderProps {
  user: { email: string; photoURL?: string } | null;
  /** The dashboard's items, which the search launcher ranks. */
  items: DashboardItem[];
  onAddClick: () => void;
  onSignOut?: () => void;
  isAdmin?: boolean;
  className?: string;
}

const controlClass =
  "tap-target h-7 inline-flex items-center gap-1 px-2 rounded-[var(--radius-sm)] text-[0.8125rem] font-medium " +
  "border border-[var(--color-border)] transition-colors focus:outline-none " +
  "focus:ring-2 focus:ring-[var(--color-accent)]";

const Header: React.FC<HeaderProps> = ({
  user,
  items,
  onAddClick,
  onSignOut,
  isAdmin,
  className,
}) => {
  const editing = useUIStore((s) => s.editing);
  const setEditing = useUIStore((s) => s.setEditing);
  const {
    query,
    setQuery,
    clear,
    rows,
    active,
    setActive,
    expanded,
    inputRef,
    openRow,
    inputHandlers,
  } = useLauncher(items);
  const listboxId = useId();

  return (
    <header
      className={`flex items-center gap-2 sm:gap-3 w-full h-[52px] px-3 sm:px-4 bg-[var(--color-bg)] border-b border-[var(--color-border)] ${className || ""}`}
    >
      <Link
        to="/"
        className="flex-shrink-0 font-heading text-lg font-semibold tracking-tight text-[var(--color-accent)] select-none hover:opacity-80 transition-opacity"
      >
        brizzly
      </Link>
      {/* Auto margins absorb the space the capped field does not use, so the
          field centres and the controls sit at the trailing edge. */}
      <div className="relative flex-1 min-w-0 max-w-[400px] mx-auto">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label="Search your links, or the web"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listboxId}
          aria-activedescendant={expanded ? launcherOptionId(listboxId, active) : undefined}
          placeholder="Search your links, or the web…"
          className="w-full px-3 py-1.5 pr-14 rounded-[var(--radius-sm)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-sm text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors"
          style={{ boxShadow: "none", appearance: "none" }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          {...inputHandlers}
        />
        {query ? (
          <button
            type="button"
            aria-label="Clear search"
            className="tap-target absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] p-1 rounded focus:outline-none"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.47 4.47a.75.75 0 0 1 1.06 0L8 6.94l2.47-2.47a.75.75 0 1 1 1.06 1.06L9.06 8l2.47 2.47a.75.75 0 0 1-1.06 1.06L8 9.06l-2.47 2.47a.75.75 0 0 1-1.06-1.06L6.94 8 4.47 5.53a.75.75 0 0 1 0-1.06z"
                fill="currentColor"
                className="transition-colors"
              />
            </svg>
          </button>
        ) : (
          // Display only: useLauncher binds the shortcut globally.
          <kbd
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-bg)] font-mono text-xs leading-none text-[var(--color-text-tertiary)]"
          >
            ⌘K
          </kbd>
        )}
        {expanded && (
          <LauncherResults
            id={listboxId}
            rows={rows}
            active={active}
            onHover={setActive}
            onChoose={openRow}
          />
        )}
      </div>
      <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2 [@media(pointer:coarse)]:gap-3">
        <button
          type="button"
          aria-label="Add to dashboard"
          onClick={onAddClick}
          className={`${controlClass} bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]`}
        >
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span className="hidden sm:inline">Add</span>
        </button>
        <button
          type="button"
          aria-label={editing ? "Done editing" : "Edit dashboard"}
          onClick={() => setEditing(!editing)}
          className={
            editing
              ? `${controlClass} border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-text-inverse)] hover:opacity-90`
              : `${controlClass} bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]`
          }
        >
          {editing ? "Done" : "Edit"}
        </button>
        {user && (
          <UserMenu user={user} onSignOut={onSignOut || (() => {})} isAdmin={isAdmin || false} />
        )}
      </div>
    </header>
  );
};

export default Header;
